import { useEffect, useState, type ReactNode } from 'react'
import Cropper, { type Area } from 'react-easy-crop'
import { Loader2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog'
import { Button } from './ui/button'
import { useCropSession } from '../hooks/useCropSession'
import { useResetOnChange } from '../hooks/useResetOnChange'
import { cancelButtonLabel, saveButtonLabel } from '../lib/commonStrings'

function canvasSupportsWebP(): boolean {
  const c = document.createElement('canvas')
  c.width = 1
  c.height = 1
  return c.toDataURL('image/webp').startsWith('data:image/webp')
}

// iOS Safari can't encode WebP: toBlob('image/webp') silently hands back a PNG.
const EXPORT_FORMAT = canvasSupportsWebP() ? 'image/webp' : 'image/jpeg'

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error('image decode failed'))
    img.src = src
  })
}

async function getCroppedBlob(
  imageSrc: string,
  area: Area,
  output: { width: number; height: number },
  quality: number,
): Promise<Blob> {
  const image = await loadImage(imageSrc)
  // createImageBitmap crops at the decode level, avoiding iOS Safari's
  // canvas subsampling of large camera photos
  const bitmap = await createImageBitmap(
    image,
    Math.round(area.x),
    Math.round(area.y),
    Math.round(area.width),
    Math.round(area.height),
  )
  const canvas = document.createElement('canvas')
  canvas.width = output.width
  canvas.height = output.height
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('canvas 2d unavailable')
  ctx.drawImage(bitmap, 0, 0, output.width, output.height)
  bitmap.close()
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      blob => (blob ? resolve(blob) : reject(new Error('toBlob failed'))),
      EXPORT_FORMAT,
      quality,
    )
  })
}

export interface ImageCropDialogProps {
  open: boolean
  imageSrc: string | null
  onCancel: () => void
  /** Receives a WebP blob, or JPEG where the browser can't encode WebP — upload with `blob.type`. */
  onConfirm: (blob: Blob) => Promise<void>
}

interface Props extends ImageCropDialogProps {
  title: ReactNode
  zoomLabel: ReactNode
  zoomAriaLabel: string
  aspect: number
  cropShape?: 'rect' | 'round'
  output: { width: number; height: number }
  quality: number
}

export function ImageCropDialog({
  open,
  imageSrc,
  onCancel,
  onConfirm,
  title,
  zoomLabel,
  zoomAriaLabel,
  aspect,
  cropShape = 'rect',
  output,
  quality,
}: Props) {
  const { session, crop, setCrop, zoom, setZoom, areaPixels, onCropComplete } =
    useCropSession(open, imageSrc)
  const [submitting, setSubmitting] = useState(false)
  const [ready, setReady] = useResetOnChange(session, () => false)

  useEffect(() => {
    if (!session) return
    // Delay mount until after the dialog entrance animation (zoom-in-80,
    // 250ms) so react-easy-crop measures the container at its final size
    const t = setTimeout(() => setReady(true), 300)
    return () => clearTimeout(t)
  }, [session, setReady])

  async function handleConfirm() {
    if (!imageSrc || !areaPixels) return
    setSubmitting(true)
    try {
      const blob = await getCroppedBlob(imageSrc, areaPixels, output, quality)
      await onConfirm(blob)
    } catch {
      // Parent surfaces the error; keep the dialog open so the user can retry.
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={next => {
        if (!next && !submitting) onCancel()
      }}
    >
      <DialogContent aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <div
          className="relative w-full overflow-hidden rounded-md bg-black"
          style={{ aspectRatio: aspect }}
        >
          {imageSrc && ready && (
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={aspect}
              cropShape={cropShape}
              showGrid={false}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          )}
        </div>

        <div className="flex items-center gap-3">
          <span className="text-xs text-muted-foreground shrink-0">{zoomLabel}</span>
          <input
            type="range"
            min={1}
            max={3}
            step={0.01}
            value={zoom}
            onChange={e => setZoom(Number(e.target.value))}
            aria-label={zoomAriaLabel}
            className="w-full accent-primary cursor-pointer"
          />
        </div>

        <DialogFooter>
          <Button variant="ghost" disabled={submitting} onClick={onCancel}>
            {cancelButtonLabel()}
          </Button>
          <Button disabled={submitting || !areaPixels} onClick={handleConfirm}>
            {submitting && <Loader2 className="size-3.5 mr-1.5 animate-spin" />}
            {saveButtonLabel()}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
